import React, { useState, useEffect, useCallback } from 'react';
import { brandCategoriesAPI } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, FolderOpen, X, Check, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';

const generateSlug = (name) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const AdminBrandCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '' });

  const fetchCategories = useCallback(async () => {
    try {
      const res = await brandCategoriesAPI.getAll();
      setCategories(res.data);
    } catch (error) {
      toast.error('Failed to load brand categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const resetForm = () => {
    setFormData({ name: '', slug: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingId ? prev.slug : generateSlug(name),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast.error('Name and slug are required');
      return;
    }
    const duplicate = categories.find(c => c.slug === formData.slug && c.id !== editingId);
    if (duplicate) {
      toast.error('A brand category with this slug already exists');
      return;
    }
    try {
      if (editingId) {
        await brandCategoriesAPI.update(editingId, formData);
        toast.success('Brand category updated');
      } else {
        await brandCategoriesAPI.create(formData);
        toast.success('Brand category created');
      }
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const startEdit = (cat) => {
    setFormData({ name: cat.name, slug: cat.slug });
    setEditingId(cat.id);
    setShowForm(true);
  };

  const handleDelete = async (cat) => {
    try {
      await brandCategoriesAPI.delete(cat.id);
      toast.success('Brand category deleted');
      setDeleteConfirm(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-brand-categories-page">
      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-gray-200 rounded-lg transition-colors" data-testid="back-to-admin">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-playfair font-bold text-gray-900" data-testid="page-title">Brand Categories</h1>
              <p className="text-sm text-gray-500 mt-1">{categories.length} categories for pSEO brands</p>
            </div>
          </div>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-brand-red text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-brand-red/90 transition-colors"
            data-testid="add-brand-category-button"
          >
            <Plus className="w-4 h-4" />
            Add Brand Category
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 shadow-sm" data-testid="brand-category-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                {editingId ? 'Edit Brand Category' : 'New Brand Category'}
              </h2>
              <button onClick={resetForm} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent"
                  placeholder="e.g. Electronics"
                  required
                  data-testid="brand-category-name-input"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-transparent font-mono text-sm"
                  placeholder="electronics"
                  required
                  data-testid="brand-category-slug-input"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-brand-red/90 transition-colors whitespace-nowrap"
                data-testid="save-brand-category-button"
              >
                <Check className="w-4 h-4" />
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2.5 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="brand-categories-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Slug</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-6 py-12 text-center text-gray-400">
                    <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-lg font-medium">No brand categories yet</p>
                  </td>
                </tr>
              ) : (
                categories.map((cat) => (
                  <tr key={cat.id} className="hover:bg-gray-50 transition-colors" data-testid={`brand-category-row-${cat.slug}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Tag className="w-4 h-4 text-amber-600" />
                        </div>
                        <span className="font-semibold text-gray-900">{cat.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">{cat.slug}</code>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => startEdit(cat)}
                          className="p-2 text-gray-500 hover:text-brand-red hover:bg-gray-100 rounded-lg transition-colors"
                          data-testid={`edit-brand-category-${cat.slug}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {deleteConfirm === cat.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(cat)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg text-xs font-semibold"
                              data-testid={`confirm-delete-brand-${cat.slug}`}
                            >
                              Confirm
                            </button>
                            <button onClick={() => setDeleteConfirm(null)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg text-xs">
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(cat.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            data-testid={`delete-brand-category-${cat.slug}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminBrandCategories;
